import Layout from "components/layout";
import SEO from "components/seo";
import React, { useCallback, useRef, useState } from "react";
import { fileExtension } from "utils/fileextension";
import http from "utils/http";
import FileLoadingInput from "components/FileUploading/fileloadinginput";
import ReUploadFile from "components/FileUploading/reUploadingfile";
import ToolsBreadcrumb from "components/ToolsBredcrumb";
import BannerImage from "assets/grammer_checker_banner.png";
import BannerSection from "components/PostTranslation/BannerSection";
import ReservedAdSlot from "components/ads/ReservedAdSlot";
import ToolFaq from "components/QualityTools/ToolFaq";
import GrammerCheckerResult from "components/Grammerchecker/GrammerCheckerResult";
import Loader from "components/loader";
import UnsupportedFilePopUp from "components/FileUploading/unsupportedPopUp";
import { io } from "socket.io-client";
import CleanButton from "assets/cleanBtn.svg";
import { useLocation } from "@reach/router";
import { shimmerData } from "components/Grammerchecker/config";
import { SocketEvents } from "utils/socketEvent";
import axios from "axios";
import { endpoints } from "utils/endpoints";
import HeroBackdrop from "utils/HeroBackdrop";
import {
  ToolHowToSection,
  buildToolHowToLdJson,
} from "utils/toolHowTo";
import { getToolHreflangAlternateLinks } from "utils/toolsHreflangMap";
import { LANG_TO_HREFLANG } from "utils/toolsSeoLocale";
import translationsText from "utils/toolsJson/grammer_checker.json";

const GrammarChecker = ({ pageContext = {} }) => {
  const lang = pageContext.lang || "en";
  const isRTL = lang === "ar" || lang === "he" || lang === "fa";
  const translationData = {
    ...translationsText.en,
    ...(translationsText[lang] || {}),
  };

  const [text, setText] = useState("");
  const [textLoader, setTextLoader] = useState(false);
  const [fileUploadLoading, setFileUploading] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [sizeLimitError, setSizeLImitError] = useState(false);
  const [error, setError] = useState(false);
  const [file, setFile] = useState(false);
  const [inputLang, setInputLang] = useState("");
  const [loader, setLoader] = useState(false);
  const [shareId, setShareId] = useState("");
  const [result, setResult] = useState(shimmerData);
  const [tooltipContent, setTooltipContent] = useState(
    translationData.copy_tooltip
  );
  const [selectedEngine, setSelectedEngine] = useState(null);
  const location = useLocation();
  const textareaRef = useRef(null);

  const seoPathSegment =
    pageContext.localizedSlug ||
    translationData?.url?.replace(/^\//, "") ||
    "tools/grammar-checker";
  const seoSlug =
    lang === "en" ? `/${seoPathSegment}` : `/${lang}/${seoPathSegment}`;

  const BaseUrl = process.env.GATSBY_PUBLIC_SOCKET_URL;
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  const uploadFile = async (e) => {
    setSizeLImitError(false);
    setUploadError(false);
    const selectedFile = e.target.files[0];
    const extension = fileExtension(selectedFile.name);
    if (!extension) {
      setUploadError(true);
      setFile(selectedFile);
      return;
    }
    setFile(selectedFile);
    if (selectedFile.size >= 15000000) {
      alert(
        translationData?.alter_message ||
          "File is too large. Please upload file less than 10 MB."
      );
      setSizeLImitError(true);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setTextLoader(true);
      setFileUploading(true);
      setUploadError(null);
      const res = await http().post(endpoints.forms.toolsFileUpload, formData);

      setText(res.data.text);
      setTextLoader(false);
    } catch (err) {
      console.log(err, "error");
      setUploadError(
        translationData?.setUploadError_text ||
          "Error uploading file. Please try again."
      );
    } finally {
      setFileUploading(false);
      setTextLoader(false);
    }
  };

  const fetchAllEnginesWithSocket = useCallback(
    (generatedShareId) => {
      if (!generatedShareId) return;

      const socket = io(BaseUrl, {
        auth: { shareId: generatedShareId },
      });

      socket.on("connect", () => {
        console.log("Socket Connected:", socket.id);
      });

      socket.emit(SocketEvents.AiTool.REQUEST, {
        shareId: generatedShareId,
        source_text: text,
        tool_name: "grammer_checker",
        llmList: [
          "chat_gpt",
          "amazon_nova",
          "claude",
          "gemini",
          "grok_ai",
          "mistral_ai",
          "qwen",
        ],
      });

      socket.on(SocketEvents.AiTool.RESPONSE, (response) => {
        if (response.data.data?.corrected_text) {
          setResult((prev = shimmerData) => {
            let updated = prev?.map((elem) => {
              if (elem.engine === response.llm) {
                return {
                  ...elem,
                  original_text: response.data.data.original_text,
                  corrected_text: response.data.data.corrected_text,
                  detected_language: response.data.data.detected_language,
                };
              }
              return elem;
            });

            if (response.llm === "smart") {
              updated = updated.filter(
                (item) => item.engine === "smart" || item.corrected_text
              );
            }

            return updated;
          });
        }
      });

      socket.on(SocketEvents.AiTool.END, (response) => {
        console.log("END:", response);
        socket.disconnect();
      });
    },
    [text, BaseUrl]
  );

  const getGrammerChecker = async () => {
    try {
      setLoader(true);
      setError("");

      const payload = {
        source_text: text,
        tool_name: "grammer_checker",
      };

      const response = await axios.post(
        `${BaseUrl}${endpoints?.forms?.grammer_checker_socket}`,
        payload
      );

      const generatedShareId = response.data?.share_id;

      if (!generatedShareId) {
        setError(
          translationData?.setError_text || "Unable to generate share id"
        );
        return;
      }

      setShareId(generatedShareId);

      fetchAllEnginesWithSocket(generatedShareId);
    } catch (err) {
      setError(
        err?.message ||
          translationData?.setError_text1 ||
          "Something went wrong"
      );
    } finally {
      setLoader(false);
    }
  };

  const shareresult = () => {
    const origin =
      location?.origin ||
      (typeof window !== "undefined" ? window.location.origin : "");
    navigator.clipboard.writeText(
      `${origin}/tools/grammar-checker/result?share-id=${shareId}`
    );
    setTooltipContent(translationData?.link_copy || "Link Copied");

    setTimeout(() => {
      setTooltipContent(translationData?.copy_tooltip || "Copy");
    }, 2000);
  };

  const howToSteps = translationData?.GRAMMAR_CHECKER_HOWTO_STEPS_LD || [];
  const howToPage =
    translationData?.GRAMMAR_CHECKER_HOWTO_PAGE ||
    "https://www.tomedes.com/tools/grammar-checker";

  return (
    <Layout>
      <SEO
        title={translationData?.seo_title}
        description={translationData?.seo_description}
        slug={seoSlug}
        alternateHreflang={LANG_TO_HREFLANG[lang] || LANG_TO_HREFLANG.en}
        htmlLang={LANG_TO_HREFLANG[lang] || LANG_TO_HREFLANG.en}
        hreflangAlternates={getToolHreflangAlternateLinks("grammar-checker")}
        scehema={[
          translationData?.grammar_checker_software_schema,
          translationData?.grammar_checker_faq_schema,
        ]}
        howToSchema={buildToolHowToLdJson(howToPage, {
          name: translationData?.howToSchema?.heading,
          description: translationData?.howToSchema?.description,
          toolName: translationData?.howToSchema?.toolName,
          steps: howToSteps,
        })}
      />
      <div
        className={`hero ${shareId ? " hero--compact" : ""}`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <HeroBackdrop />
        <div
          className={`hero-main max-w-7xl mx-auto p-4 ${
            shareId ? "pt-12 pb-24" : "py-24"
          }`}
        >
          {uploadError && !fileUploadLoading && (
            <UnsupportedFilePopUp
              file={file}
              setSizeLImitError={setSizeLImitError}
              setFile={setFile}
              uploadFile={uploadFile}
              setFileUploadError={setUploadError}
              translationData={translationData}
            />
          )}
          {!shareId ? (
            <div className="max-w-7xl mx-auto p-4">
              <h1 className="text-[#0A2641] font-semibold text-center text-[28px] md:text-[40px] leading-[48px] font-primary">
                {translationData?.heading}
              </h1>

              <p className="text-center font-opensans mt-4 text-xl font-normal text-[#656565]">
                {translationData?.subHeading}
              </p>
              <div className="relative max-w-4xl mx-auto mt-8 p-3 bg-white border-1 border-[#D7D7D7] shadow-lg rounded-2xl ">
                {fileUploadLoading ? (
                  <FileLoadingInput
                    file={file}
                    translationData={translationData}
                  />
                ) : sizeLimitError && !fileUploadLoading ? (
                  <ReUploadFile
                    file={file}
                    setFile={setFile}
                    setSizeLImitError={setSizeLImitError}
                    uploadFile={uploadFile}
                    translationData={translationData}
                  />
                ) : (
                  <>
                    <div className="w-full grid">
                      <textarea
                        ref={textareaRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder=""
                        className="w-full min-h-[130px] pe-3 border-none outline-none placeholder-[#7A7A7A] text-black resize-none pt-10 [grid-area:1/1]"
                        dir={isRTL ? "rtl" : "ltr"}
                      />

                      {(!text || textLoader) && (
                        <div className="px-1 pt-2 text-[#7A7A7A] [grid-area:1/1] pointer-events-none">
                          <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                            {textLoader ? (
                              <span className="text-[#B4B4B4] italic">
                                {translationData?.loading_placeholder_text}
                              </span>
                            ) : (
                              <>
                                <span className="text-[#7A7A7A]">
                                  {translationData?.enter_text}
                                </span>
                                <label
                                  htmlFor="uploadFile"
                                  className="cursor-pointer font-medium text-[#0082FF] pointer-events-auto"
                                >
                                  {translationData?.upload_file_text}
                                </label>
                                <input
                                  type="file"
                                  className="hidden"
                                  id="uploadFile"
                                  onChange={uploadFile}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {text && (
                      <button
                        onClick={() => setText("")}
                        className="absolute top-[23px] text-white end-[11px]"
                      >
                        <img
                          src={CleanButton}
                          alt={
                            translationData?.ui?.clean_button_alt ||
                            "clean-text-button"
                          }
                        />
                      </button>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                      {text && (
                        <span className="text-sm text-gray-500 inline-flex items-center gap-1">
                          {isRTL ? (
                            <>
                              <bdi className="tabular-nums">{wordCount}</bdi>
                              <span>
                                {translationData?.word ||
                                  translationData?.word_count_text}
                              </span>
                            </>
                          ) : (
                            <>
                              <span>{translationData?.word_count_label}</span>
                              <bdi className="tabular-nums">{wordCount}</bdi>
                            </>
                          )}
                        </span>
                      )}

                      <div className="flex shrink-0 justify-end gap-3">
                        <button
                          disabled={!text}
                          onClick={getGrammerChecker}
                          className="px-6 py-2 rounded-full bg-[#5B93FF] text-white font-medium  "
                        >
                          {loader ? (
                            <Loader />
                          ) : (
                            translationData?.button_text
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <GrammerCheckerResult
              title={translationData?.heading}
              result={result}
              tooltipContent={tooltipContent}
              error={error}
              shareresult={shareresult}
              inputLang={inputLang}
              shareId={shareId}
              file={file}
              setResult={setResult}
              selectedEngine={selectedEngine}
              setSelectedEngine={setSelectedEngine}
              setShareId={setShareId}
              setText={setText}
              smartTooltipText={translationData?.smart_tooltip}
              smart_text={translationData?.smart_text}
              best_text={translationData?.best_text}
              original_text={translationData?.original_text}
              corrected_text={translationData?.corrected_text}
              wordtext={translationData?.word}
              copy_text={translationData?.copy_tooltip}
              copy_text_success={translationData?.copy_text_success}
              link_copy={translationData?.link_copy}
              download_tooltip={translationData?.download_tooltip}
              back_link={
                translationData?.navigate_url ||
                translationData?.url ||
                "/tools/grammar-checker/"
              }
            />
          )}
        </div>
      </div>
      <div className="bg-white " dir={isRTL ? "rtl" : "ltr"}>
        <div className="pt-7">
          <ToolsBreadcrumb
            widget={true}
            secureText={translationData?.bredcrumbs_text}
            bredcrumbTooltip={translationData?.bredcrumbTooltip}
            tools={translationData?.tool_breadcrumbs}
            toolName={translationData?.tool_breadcrumbs_name}
            toolmainLink={
              translationData?.toolmainLink || translationData?.toolslink
            }
            toolLink={translationData?.tool_link || translationData?.url}
            result={shareId}
            setResult={setShareId}
            resultText={translationData?.result_text}
          />
        </div>
        <div className="max-w-7xl mx-auto p-4">
          <ReservedAdSlot toolId={pageContext?.toolId || "grammar-checker"} />
          <BannerSection
            title={translationData?.bannerSectionTitle}
            description={translationData?.banner_description}
            heading={translationData?.banner_heading_text}
            BannerData={translationData?.banner_data}
            BannerImage={BannerImage}
            text={translationData?.disclaimer}
            isTranslatoir={true}
            ispostTranslation
            isGrammerChecker
          />
        </div>
        <ToolHowToSection
          sectionId={
            translationData?.how_to_section_id || "how-to-grammar-checker"
          }
          heading={translationData?.howToSchema?.heading}
          description={translationData?.howToSchema?.description}
          steps={howToSteps.map((s, i) => ({
            id: `step-${i + 1}`,
            lead: `${s.name}.`,
            body: s.text,
          }))}
        />
        <ToolFaq
          translationData={translationData}
          QaToolFaqData={translationData?.toolFaq_data}
        />
      </div>
    </Layout>
  );
};

export default GrammarChecker;
